<template>
<div>
    <h1>Rapports</h1>

    <div class="toolbar">
        <label for="modeSelect">Mode</label>
        <DropdownView id="modeSelect" v-model="selectedMode" :options="modeOptions" optionLabel="label" optionValue="value" @change="onModeChange" />

        <template v-if="selectedMode !== 'range'">
          <label for="yearSelect">Année</label>
          <DropdownView id="yearSelect" v-model="selectedYear" :options="yearOptions" @change="loadCurrentReport" />
        </template>

        <template v-else>
          <label for="fromDate">Du</label>
          <CalendarView id="fromDate" v-model="rangeFrom" :manualInput="false" dateFormat="yy-mm-dd" />
          <label for="toDate">Au</label>
          <CalendarView id="toDate" v-model="rangeTo" :manualInput="false" dateFormat="yy-mm-dd" />
        </template>

        <InputButton icon="pi pi-refresh" label="Rafraîchir" class="p-button-outlined p-button-secondary" @click="loadCurrentReport"/>
    </div>

    <GridLoader v-if="reportingLoading" class="loader"></GridLoader>

    <div v-if="!reportingLoading && selectedMode === 'monthly'">
        <h3>Résumé mensuel {{ selectedYear }}</h3>
        <DataTable :value="filteredMonthlyRows" :rowClass="monthlyRowClass">
            <DataTableColumn field="monthLabel" header="Mois"></DataTableColumn>
            <DataTableColumn field="appointments" header="RDV"></DataTableColumn>
            <DataTableColumn field="clients" header="Clients"></DataTableColumn>
            <DataTableColumn field="avgDuration" header="Durée moy. (min)"></DataTableColumn>
            <DataTableColumn field="serviceSales" header="Services (CHF)"></DataTableColumn>
            <DataTableColumn field="productSales" header="Ventes (CHF)"></DataTableColumn>
            <DataTableColumn field="avgRevenuePerHour" header="CHF / heure"></DataTableColumn>
        </DataTable>
    </div>

    <div v-if="!reportingLoading && selectedMode === 'weekly'">
        <h3>Détail hebdomadaire {{ selectedYear }}</h3>
        <DataTable :value="weeklyRows" :rowClass="weeklyRowClass">
            <DataTableColumn field="weekLabel" header="Semaine"></DataTableColumn>
            <DataTableColumn field="appointments" header="RDV"></DataTableColumn>
            <DataTableColumn field="clients" header="Clients"></DataTableColumn>
            <DataTableColumn field="avgDuration" header="Durée moy. (min)"></DataTableColumn>
            <DataTableColumn field="serviceSales" header="Services (CHF)"></DataTableColumn>
            <DataTableColumn field="productSales" header="Ventes (CHF)"></DataTableColumn>
            <DataTableColumn field="avgRevenuePerHour" header="CHF / heure"></DataTableColumn>
        </DataTable>
    </div>

    <div v-if="!reportingLoading && selectedMode === 'range'">
        <h3>Période libre</h3>
        <h6 v-if="!rangeMetricsRows.length">Aucune donnée pour cette période.</h6>
        <DataTable v-if="rangeMetricsRows.length" :value="rangeMetricsRows">
            <DataTableColumn field="label" header="Indicateur"></DataTableColumn>
            <DataTableColumn field="value" header="Valeur"></DataTableColumn>
        </DataTable>
    </div>
</div>
</template>

<style scoped>
.toolbar {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  margin: 15px 0 20px 0;
}

.toolbar :deep(.p-calendar) {
  min-width: 160px;
}

:deep(.current-period-row > td) {
  background: #f0f7ff !important;
  font-weight: 600;
}

:deep(.total-row > td) {
  background: #f1f5f9 !important;
  font-weight: 700;
  border-top: 2px solid #cbd5e1;
}
</style>

<script setup>
import { ref, computed, onMounted } from 'vue';
import axios from 'axios';
import { authHeader } from '../helpers/authHeader.js';

const reportingLoading = ref(false);
const monthlyRows = ref([]);
const weeklyRows = ref([]);
const rangeMetrics = ref({});

const currentYear = new Date().getFullYear();
const selectedMode = ref('monthly');
const modeOptions = ref([
  { label: 'Mensuel', value: 'monthly' },
  { label: 'Hebdomadaire détaillé', value: 'weekly' },
  { label: 'Période libre', value: 'range' }
]);
const selectedYear = ref(currentYear);
const yearOptions = ref(Array.from({ length: 7 }, (_, i) => currentYear - 3 + i));
const rangeFrom = ref(new Date(currentYear, new Date().getMonth(), 1));
const rangeTo = ref(new Date());

const monthNames = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

onMounted(() => {
  loadCurrentReport();
});

function toNumber(value) {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : 0;
  }

  const normalized = String(value ?? '')
    .trim()
    .replace(/\s/g, '')
    .replace(',', '.');
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
}

function normalizeRow(row) {
  const monthIndex = Math.max(1, Math.min(12, toNumber(row.month))) - 1;
  return {
    month: monthIndex + 1,
    monthLabel: monthNames[monthIndex],
    appointments: toNumber(row.appointments),
    clients: toNumber(row.clients),
    avgDuration: toNumber(row.avgDuration).toFixed(2),
    serviceSales: toNumber(row.serviceSales).toFixed(2),
    productSales: toNumber(row.productSales).toFixed(2),
    avgRevenuePerHour: row.avgRevenuePerHour === 'NA' ? 'NA' : toNumber(row.avgRevenuePerHour).toFixed(2)
  };
}

function normalizeWeeklyRow(row) {
  return {
    weekLabel: `S${toNumber(row.week)} (${row.weekStart} au ${row.weekEnd})`,
    weekStart: row.weekStart,
    weekEnd: row.weekEnd,
    appointments: toNumber(row.appointments),
    clients: toNumber(row.clients),
    avgDuration: toNumber(row.avgDuration).toFixed(2),
    serviceSales: toNumber(row.serviceSales).toFixed(2),
    productSales: toNumber(row.productSales).toFixed(2),
    avgRevenuePerHour: row.avgRevenuePerHour === 'NA' ? 'NA' : toNumber(row.avgRevenuePerHour).toFixed(2)
  };
}

function formatDate(date) {
  return `${date.getFullYear()}-${`0${date.getMonth() + 1}`.slice(-2)}-${`0${date.getDate()}`.slice(-2)}`;
}

function formatMetricValue(key, value) {
  if (value === 'NA') {
    return 'NA';
  }

  if (['serviceSales', 'productSales', 'avgRevenuePerHour'].includes(key)) {
    return `${toNumber(value).toFixed(2)} CHF`;
  }

  if (key === 'avgDuration') {
    return `${toNumber(value).toFixed(2)} min`;
  }

  if (key === 'avgAppointmentsPerClient') {
    return `${toNumber(value).toFixed(2)}`;
  }

  return `${toNumber(value)}`;
}

function metricLabel(key) {
  const labels = {
    clients: 'Clients uniques',
    appointments: 'Rendez-vous',
    avgAppointmentsPerClient: 'RDV moyens / client',
    avgDuration: 'Durée moyenne',
    serviceSales: 'CA services',
    productSales: 'CA ventes',
    avgRevenuePerHour: 'CA services / heure'
  };
  return labels[key] || key;
}

const rangeMetricsRows = ref([]);

const filteredMonthlyRows = computed(() => {
  const now = new Date();
  const isCurrentYear = Number(selectedYear.value) === now.getFullYear();
  const isFutureYear = Number(selectedYear.value) > now.getFullYear();

  if (isFutureYear) return [];

  const rows = isCurrentYear
    ? monthlyRows.value.filter(r => Number(r.month) <= now.getMonth() + 1)
    : monthlyRows.value;

  if (rows.length === 0) return [];

  const totalApps = rows.reduce((s, r) => s + toNumber(r.appointments), 0);
  const totalClients = rows.reduce((s, r) => s + toNumber(r.clients), 0);
  const totalService = rows.reduce((s, r) => s + toNumber(r.serviceSales), 0);
  const totalProduct = rows.reduce((s, r) => s + toNumber(r.productSales), 0);
  const totalDurationSum = rows.reduce((s, r) => s + toNumber(r.avgDuration) * toNumber(r.appointments), 0);
  const avgDuration = totalApps > 0 ? totalDurationSum / totalApps : 0;
  const totalHours = totalDurationSum / 60;
  const avgRevPerHour = totalHours > 0 ? totalService / totalHours : 0;

  return [
    ...rows,
    {
      month: 99,
      monthLabel: 'Total',
      appointments: totalApps,
      clients: totalClients,
      avgDuration: avgDuration.toFixed(2),
      serviceSales: totalService.toFixed(2),
      productSales: totalProduct.toFixed(2),
      avgRevenuePerHour: avgRevPerHour.toFixed(2),
      _isTotal: true
    }
  ];
});

function buildRangeMetricsRows(data) {
  const order = ['clients', 'appointments', 'avgAppointmentsPerClient', 'avgDuration', 'serviceSales', 'productSales', 'avgRevenuePerHour'];
  return order
    .filter((key) => data[key] !== undefined)
    .map((key) => ({
      label: metricLabel(key),
      value: formatMetricValue(key, data[key])
    }));
}

function onModeChange() {
  loadCurrentReport();
}

function monthlyRowClass(data) {
  if (data._isTotal) return 'total-row';
  const now = new Date();
  const isCurrentYear = Number(selectedYear.value) === now.getFullYear();
  const isCurrentMonth = Number(data.month) === (now.getMonth() + 1);
  return isCurrentYear && isCurrentMonth ? 'current-period-row' : '';
}

function weeklyRowClass(data) {
  const now = new Date();
  const isCurrentYear = Number(selectedYear.value) === now.getFullYear();
  if (!isCurrentYear || !data.weekStart || !data.weekEnd) {
    return '';
  }

  const from = new Date(`${data.weekStart}T00:00:00`);
  const to = new Date(`${data.weekEnd}T23:59:59`);
  return now >= from && now <= to ? 'current-period-row' : '';
}

function loadCurrentReport() {
  if (selectedMode.value === 'monthly') {
    loadMonthlyReporting();
    return;
  }

  if (selectedMode.value === 'weekly') {
    loadWeeklyReporting();
    return;
  }

  loadRangeReporting();
}

function loadMonthlyReporting() {
  reportingLoading.value = true;
  monthlyRows.value = [];
  weeklyRows.value = [];
  rangeMetrics.value = {};
  rangeMetricsRows.value = [];

  var options = {
    "headers": authHeader()
  };

  axios
    .get(`${process.env.VUE_APP_API_URL}/Reporting/monthly?year=${selectedYear.value}`, options)
    .then(response => {
      monthlyRows.value = response.data.map(normalizeRow);
      reportingLoading.value = false;
    })
    .catch(reason => {
      reportingLoading.value = false;
      console.log(reason);
      alert("error");
    });
}

function loadWeeklyReporting() {
  reportingLoading.value = true;
  monthlyRows.value = [];
  weeklyRows.value = [];
  rangeMetrics.value = {};
  rangeMetricsRows.value = [];

  var options = {
    "headers": authHeader()
  };

  axios
    .get(`${process.env.VUE_APP_API_URL}/Reporting/weekly?year=${selectedYear.value}`, options)
    .then(response => {
      weeklyRows.value = response.data.map(normalizeWeeklyRow);
      reportingLoading.value = false;
    })
    .catch(reason => {
      reportingLoading.value = false;
      console.log(reason);
      alert("error");
    });
}

function loadRangeReporting() {
  if (!rangeFrom.value || !rangeTo.value) {
    alert("Merci de choisir une période valide.");
    return;
  }

  if (rangeTo.value < rangeFrom.value) {
    alert("La date de fin doit être après la date de début.");
    return;
  }

  reportingLoading.value = true;
  monthlyRows.value = [];
  weeklyRows.value = [];
  rangeMetrics.value = {};
  rangeMetricsRows.value = [];

  var options = {
    "headers": authHeader()
  };

  const from = formatDate(rangeFrom.value);
  const to = formatDate(rangeTo.value);

  axios
    .get(`${process.env.VUE_APP_API_URL}/Reporting?from=${from}&to=${to}`, options)
    .then(response => {
      rangeMetrics.value = response.data || {};
      rangeMetricsRows.value = buildRangeMetricsRows(rangeMetrics.value);
      reportingLoading.value = false;
    })
    .catch(reason => {
      reportingLoading.value = false;
      console.log(reason);
      alert("error");
    });
}
</script>
